import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { HandHeart, Receipt } from "@phosphor-icons/react";

const CATEGORIES = [
  { v: "monthly", l: "Monthly Sponsorship", amt: 2500 },
  { v: "birthday", l: "Birthday Gift", amt: 1000 },
  { v: "festival", l: "Festival Celebration", amt: 500 },
  { v: "medical", l: "Medical Support", amt: 0 },
  { v: "boys_hostel", l: "Boys Hostel", amt: 5000 },
  { v: "girls_hostel", l: "Girls Hostel", amt: 5000 },
  { v: "other_contribution", l: "Other Contribution", amt: 0 },
];

export default function Donations() {
  const [params] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [donations, setDonations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: params.get("student_id") || "", category: "monthly", amount: 2500, method: "upi" });

  const refresh = () => api.get("/donor/donations").then((r) => setDonations(r.data));
  useEffect(() => {
    refresh();
    api.get("/donor/me/students").then((r) => setStudents(r.data));
  }, []);

  const onCategoryChange = (v) => {
    const cat = CATEGORIES.find((c) => c.v === v);
    setForm((f) => ({ ...f, category: v, amount: cat?.amt || f.amount }));
  };

  const donate = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/donor/donate", { ...form, amount: Number(form.amount) });
      toast.success(`Captured • ${res.data.receipt_no_80g}`);
      setOpen(false);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <DashboardLayout title="Donations & 80G Receipts" subtitle="Mock Razorpay flow. Auto-numbered 80G receipts.">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-[var(--vs-text-secondary)]">{donations.length} donations captured</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-donation-btn" className="bg-[var(--vs-primary)] hover:bg-[var(--vs-primary-hover)] text-[var(--vs-text)]">
              <HandHeart size={14} className="mr-1.5" /> Make a contribution
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New contribution</DialogTitle></DialogHeader>
            <form onSubmit={donate} className="space-y-3">
              <div>
                <Label>Sponsored student (optional)</Label>
                <Select value={form.student_id || "none"} onValueChange={(v) => setForm({ ...form, student_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="General fund" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">General fund</SelectItem>
                    {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.vs_id} — {s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={onCategoryChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l} {c.amt > 0 ? `— ₹${c.amt}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount (₹)</Label><Input data-testid="donation-amount" required type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
                <div>
                  <Label>Method</Label>
                  <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="neft">NEFT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="donation-submit" type="submit" className="w-full bg-[var(--vs-primary)] hover:bg-[var(--vs-primary-hover)] text-[var(--vs-text)]">
                <Receipt size={14} className="mr-1.5" /> Capture (mock Razorpay)
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="vs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]"><tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
            <th className="px-4 py-3">80G Receipt</th>
            <th className="px-4 py-3">Category</th>
            <th className="px-4 py-3">Student</th>
            <th className="px-4 py-3">Amount</th>
            <th className="px-4 py-3">Method</th>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Status</th>
          </tr></thead>
          <tbody>
            {donations.map((d) => (
              <tr key={d.id} data-testid={`donation-row-${(d.receipt_no_80g || d.id || "pending").replace(/\//g, "-")}`} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-3 font-mono text-xs">{d.receipt_no_80g || <span className="text-[var(--vs-text-secondary)]">Pending</span>}</td>
                <td className="px-4 py-3 capitalize">{d.category}</td>
                <td className="px-4 py-3 font-mono text-xs">{d.student_vs_id || "General"}</td>
                <td className="px-4 py-3 font-mono">₹{d.amount}</td>
                <td className="px-4 py-3 uppercase font-mono text-xs">{d.method}</td>
                <td className="px-4 py-3 font-mono text-xs">{new Date(d.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3"><Badge variant="outline" className="border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10">{d.status}</Badge></td>
              </tr>
            ))}
            {donations.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--vs-text-secondary)]">No donations yet — make your first contribution.</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
